import { makeStyles } from '@material-ui/styles'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import type Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import ApiClient from 'api-client/ApiClient'
import ServiceUnhealthyInfo from 'app/plugins/openstack/components/common/ServiceUnhealthyInfo'
import { listFlavors } from 'app/plugins/openstack/components/flavors/actions'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { waitSeconds } from 'app/plugins/openstack/components/infrastructure/blueprint/helpers'
import { listNetworks } from 'app/plugins/openstack/components/networks/actions'
import { networksSelector } from 'app/plugins/openstack/components/networks/selectors'
import { sshKeysSelector } from 'app/plugins/openstack/components/security/keys/selectors'
import { listStoragePools } from 'app/plugins/openstack/components/storage/pools/actions'
import { storagePoolsSelector } from 'app/plugins/openstack/components/storage/pools/selectors'
import { type RootState, useAppSelector } from 'app/store'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import AddNodeGroups from 'k8s/components/capacity-and-health/AddNodeGroups'
import type { NodePoolErrors } from 'k8s/components/cluster/deployment/form-components/ServerConfigurationFields'
import { listKaapiClusters, updateNodePools } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from 'k8s/components/kaapi/clusters/selectors'
import { listKaapiMachineDeployments } from 'k8s/components/kaapi/machine-deployment/actions'
import DataKeys from 'k8s/DataKeys'
import { isNilOrEmpty } from 'utils/fp'
import { ProgressStep, useClusterProgress } from '../access-control/hooks/useClusterProgress'
import { DEFAULT_VOLUME_SIZE } from '../cluster/deployment/form-components/helpers'
import { createNodeGroupTemplateBody } from '../kaapi/clusters/action-helpers'
import { kaapiMachineDeploymentsForClusterSelector } from '../kaapi/machine-deployment/selectors'

const { kubernetes } = ApiClient.getInstance()

const validateNodePools = (nodePools) => {
  const errors = {}
  nodePools.forEach(({ id, flavor, replicas }) => {
    if (!flavor || !replicas) {
      errors[id] = errors[id] || {}
      if (!flavor) {
        errors[id]['flavor'] = 'A VM flavor selection is required.'
      }
      if (!replicas) {
        errors[id]['replicas'] = 'The value must be greater or equal than 1'
      }
    }
  })
  return errors
}

const initialValues = {
  nodePools: [{ id: 1, volumeSize: DEFAULT_VOLUME_SIZE }],
  masterNodePools: [{ id: 1, volumeSize: DEFAULT_VOLUME_SIZE }],
  openstackCluster: '',
  imageName: '',
  k8sVersion: '',
  sourceType: 'image',
  volumeSize: DEFAULT_VOLUME_SIZE,
  volumeType: '',
  sshKey: null,
}

const AddNodeGroupsModal = (props) => {
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const { progressDetails, updateProgress, resetProgress } = useClusterProgress({
    initLabel: 'Updating Cluster',
  })

  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [nodePoolErrors, setNodePoolErrors] = useState<NodePoolErrors>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Get cluster name from global preferences
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')
  const clusterName: string = useMemo(
    () => k8sPluginGlobalPerfs?.cluster as string,
    [k8sPluginGlobalPerfs.cluster],
  )
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()

  useListAction(listKaapiClusters)
  const clusters = useSelector(kaapiClustersOverviewSelector)

  const clusterDetails = useMemo(() => {
    return clusters?.find((cluster) => cluster?.metadata?.name === clusterName)
  }, [clusters, clusterName])

  // Fetch existing Machine Deployments
  const { reload: reloadMachineDeployments } = useListAction(listKaapiMachineDeployments)
  const machineDeploymentSelector = useMemo(
    () =>
      kaapiMachineDeploymentsForClusterSelector({
        clusterName: k8sPluginGlobalPerfs.cluster,
      }),
    [k8sPluginGlobalPerfs.cluster],
  )
  const initialMachineDeployments = useSelector(machineDeploymentSelector)

  // VM Flavors
  const { loading: loadingVmFlavors } = useListAction(listFlavors, {
    params,
  })
  // Filter out unsupported VM Flavors
  const vmFlavors = useAppSelector(flavorsSelector)

  const filteredFlavors = useMemo(() => {
    return vmFlavors?.filter((vm) => vm?.vcpus > 0 && vm?.ram > 0)
  }, [vmFlavors])

  // Load ssh keys
  const sshKeys = useSelector(sshKeysSelector)

  // Storage Pools
  useListAction(listStoragePools)
  const storagePools = useAppSelector(storagePoolsSelector)

  // Networks
  useListAction(listNetworks)
  const allNetworks = useAppSelector(networksSelector)
  // Filter networks by tenant
  const networksInTenant = useMemo(() => {
    return allNetworks?.filter((network) => {
      return network?.project_id === currentTenant || network?.shared
    })
  }, [allNetworks, currentTenant])

  // Create Actions
  const {
    update: createNodePools,
    updating: creatingNodePools,
    error: createNodePoolsError,
  } = useUpdateAction(updateNodePools)

  const hasError = !!createNodePoolsError

  useEffect(() => {
    if (!hasError) return
    const errorObj = error || createNodePoolsError

    setError({
      title: errorObj?.title || 'Error creating cluster',
      message:
        errorObj?.message || 'An error occurred while creating the cluster. Please try again.',
    })
  }, [hasError, createNodePoolsError])

  const handleSubmit = async () => {
    setError(null)
    setLoading(true)
    setShowProgress(!!clusterDetails?.spec?.topology)

    const nodePoolErrors = validateNodePools(params.nodePools)
    setNodePoolErrors(nodePoolErrors)
    if (
      !isNilOrEmpty(nodePoolErrors) ||
      !params.imageName ||
      !params.openstackCluster ||
      !params?.sshKey
    ) {
      setLoading(false)
      setShowProgress(false)
      setError({
        title: 'Error',
        message: 'Please fill all the required fields.',
      })
      updateProgress(ProgressStep.Init, ProgressStatus.Failed)
      return
    }

    // Generate a short UUID
    const shortUUID = uuidv4().slice(0, 8)
    const name = `md-${shortUUID}`

    if (clusterDetails?.spec?.topology) {
      const nodePoolsBody = createNodeGroupTemplateBody({
        openstackCluster: params.openstackCluster,
        nodePool: params?.nodePools[0],
        volumeType: params?.volumeType,
        volumeSize: params?.volumeSize,
        sourceType: params?.sourceType,
        image: params?.imageName,
        name,
        sshKey: sshKeys.find((key) => key.name === params?.sshKey),
        isControlPlane: clusterDetails?.controlPlaneType === 'Colocated',
      })
      updateProgress(ProgressStep.Init, ProgressStatus.Success)

      try {
        const { response: updatedNodeGroups, success } = await createNodePools({
          namespace,
          body: nodePoolsBody,
          clusterName,
        })

        if (!success || hasError) {
          throw new Error('Failed to create node group')
        }
        // Reload the machine deployments and machines
        updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)
        let isNodeGroupAdded = false
        const maxAttempts = 60 // 5 minutes timeout (60 * 5 seconds)
        let attempts = 0

        while (!isNodeGroupAdded && attempts < maxAttempts) {
          attempts++
          const machineDeployments = await kubernetes.getAllCustomResources(
            DataKeys.KaapiMachineDeployments,
          )
          const clusterMachines = machineDeployments?.filter(
            (md) => md?.spec?.clusterName === clusterName,
          )
          const newMachine =
            clusterMachines?.find(
              (md) =>
                !initialMachineDeployments.some(
                  (md2) => md2?.metadata?.name === md?.metadata?.name,
                ),
            ) || {}
          if (
            clusterMachines?.length ===
              updatedNodeGroups?.spec?.topology?.workers?.machineDeployments?.length &&
            newMachine?.status?.phase
          ) {
            isNodeGroupAdded = true
            updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
            reloadMachineDeployments(true, false)
            setIsProgressComplete(true)
            setShowProgress(false)
            setLoading(false)
          } else {
            await waitSeconds(5)
          }
        }

        if (!isNodeGroupAdded) {
          throw new Error('Node groups are taking too long to get deployed')
        }
      } catch (err) {
        setLoading(false)
        updateProgress(ProgressStep.ControlPlane, ProgressStatus.Failed)
        setError({
          title: 'Error',
          message: 'Something went wrong, please try again.',
        })
        console.error(err)
        return
      }
    } else {
      setLoading(false)
      setShowProgress(false)
      setError({
        title: 'Error',
        message: 'This cluster does not support clusterclass.',
      })
    }
  }

  const submitting = creatingNodePools || loading

  const handleNodePoolChange = useCallback(
    (id) => (newValues) => {
      const updatedNodePools = params.nodePools.map((nodePool) => {
        if (nodePool.id === id) {
          return { ...nodePool, ...newValues }
        }
        return nodePool
      })
      getParamsUpdater('nodePools')(updatedNodePools)
    },
    [params.nodePools, getParamsUpdater],
  )

  const handleReset = (shouldClose = false) => {
    setShowProgress(false)
    setIsProgressComplete(false)
    resetProgress()
    if (shouldClose) props.onClose()
  }

  const emptyDeleteHandler = useCallback(() => {}, [])

  return (
    <ModalForm
      {...props}
      open
      title="Add Node Group"
      maxWidth={820}
      className={classes.modal}
      onSubmit={showProgress || isProgressComplete ? () => handleReset(true) : handleSubmit}
      submitTitle={showProgress || isProgressComplete ? 'Finish' : 'Add Node Group'}
      error={error}
      submitting={submitting}
      onClose={props.onClose}
      onBackButtonClick={() => handleReset(false)}
      showBackButton={hasError && showProgress}
      data-testid="add-node-group-modal"
    >
      {!showProgress && !isProgressComplete ? (
        <>
          <ServiceUnhealthyInfo
            action="Node Group Creation"
            hypervisor
            imageLibrary
            blockStorage
            network
          />
          <AddNodeGroups
            nodePool={params.nodePools[0]}
            params={params}
            vmFlavors={filteredFlavors}
            storagePools={storagePools}
            networks={networksInTenant}
            showDeleteButton={false}
            onDeleteNodePool={emptyDeleteHandler}
            errors={nodePoolErrors[params.nodePools[0].id]}
            showOpenstackCluster
            getParamsUpdater={getParamsUpdater}
            onChange={handleNodePoolChange(params.nodePools[0].id)}
            k8sVersion={props?.k8sVersion}
            loadingVmFlavors={loadingVmFlavors}
          />
        </>
      ) : (
        <StepProgress
          progressDetails={progressDetails}
          title={`Node group is getting added. 
              Please wait and do not close this dialog, or the process will be cancelled.`}
        />
      )}
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
}))

export default AddNodeGroupsModal
